import { useEffect, useState } from "react";
import { Check, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/apiAdmin";
import { HUD, timeAgo } from "./theme";

const STATUS = {
  draft: "border-amber-400/40 text-amber-300",
  sent: "border-emerald-400/40 text-emerald-300",
  stub: "border-cyan-400/40 text-cyan-300",
  failed: "border-red-400/40 text-red-300",
  rejected: "border-gray-500/40 text-gray-400",
};

const Draft = ({ d, onDone }) => {
  const [text, setText] = useState(d.content);
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    setBusy(true);
    try {
      const r = await apiPost(`/admin/jarvis/outbox/${d.msg_id}/${kind}`, kind === "approve" ? { content: text } : {});
      toast.success(kind === "approve" ? `Text ${r.status === "stub" ? "logged (STUB)" : r.status}` : "Draft rejected");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`${HUD.panel} p-4 space-y-3`} data-testid={`jarvis-draft-${d.msg_id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-white">To {d.to_name || d.to} <span className="text-gray-500">{d.to_name ? `· ${d.to}` : ""}</span></p>
        <span className={`${HUD.mono} text-[10px] text-gray-500`}>{d.source?.replace(/_/g, " ")} · {timeAgo(d.created_at)}</span>
      </div>
      {d.in_reply_to && <p className="text-xs text-gray-400 italic border-l-2 border-cyan-400/30 pl-2">Customer: “{d.in_reply_to}”</p>}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid={`jarvis-draft-text-${d.msg_id}`} className={`w-full rounded-xl p-3 text-sm border ${HUD.input}`} />
      <div className="flex gap-2 justify-end">
        <button onClick={() => act("reject")} disabled={busy} data-testid={`jarvis-draft-reject-${d.msg_id}`} className="h-9 px-4 rounded-full border border-white/10 text-gray-400 hover:text-white text-sm flex items-center gap-1">
          <X className="w-3.5 h-3.5" /> Reject
        </button>
        <button onClick={() => act("approve")} disabled={busy} data-testid={`jarvis-draft-approve-${d.msg_id}`} className={`${HUD.btn} h-9 px-4 text-sm flex items-center gap-1`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Approve & send</>}
        </button>
      </div>
    </div>
  );
};

const NewDraft = ({ onDone }) => {
  const [f, setF] = useState({ to: "", to_name: "", content: "" });
  const save = async () => {
    try {
      await apiPost("/admin/jarvis/outbox", f);
      setF({ to: "", to_name: "", content: "" });
      toast.success("Draft created");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  return (
    <div className={`${HUD.panel} p-4 grid sm:grid-cols-[1fr_1fr_2fr_auto] gap-2`}>
      <input placeholder="Phone" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="jarvis-newdraft-to" className={`h-10 rounded-full px-4 text-sm border ${HUD.input}`} />
      <input placeholder="Name (optional)" value={f.to_name} onChange={(e) => setF({ ...f, to_name: e.target.value })} data-testid="jarvis-newdraft-name" className={`h-10 rounded-full px-4 text-sm border ${HUD.input}`} />
      <input placeholder="Message" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="jarvis-newdraft-content" className={`h-10 rounded-full px-4 text-sm border ${HUD.input}`} />
      <button onClick={save} disabled={!f.to || !f.content} data-testid="jarvis-newdraft-save" className={`${HUD.btn} h-10 px-4 text-sm flex items-center justify-center gap-1 disabled:opacity-40`}>
        <Plus className="w-4 h-4" /> Draft
      </button>
    </div>
  );
};

export const JarvisApprovals = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const load = async () => {
    const d = await apiGet("/admin/jarvis/outbox");
    apiGet("/admin/jarvis/requests").then((x) => setRequests(x.items || [])).catch(() => {});
    setItems(d.items || []);
    setLoading(false);
    onChange?.();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const drafts = items.filter((i) => i.status === "draft");
  const decideReq = async (code, d) => {
    const r = await apiPost(`/admin/jarvis/requests/${code}/${d}`, {});
    toast.success(r.summary || r.status);
    load();
  };
  const history = items.filter((i) => i.status !== "draft");
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />;
  return (
    <div className="space-y-5" data-testid="jarvis-approvals">
      <p className={`${HUD.label}`}>Draft → approve → send</p>
      {requests.filter((r) => r.status === "pending").map((r) => (
        <div key={r.code} className={`${HUD.panel} p-4 flex flex-wrap items-center gap-3`} data-testid={`jarvis-request-${r.code}`}>
          <p className="flex-1 text-sm text-white">{r.requester} wants to <b>{r.action}</b> <span className="text-gray-400 text-xs">{JSON.stringify(r.params).slice(0, 140)}</span> <span className="text-amber-300 text-xs">code {r.code}</span></p>
          <button onClick={() => decideReq(r.code, "deny")} className="h-9 px-4 rounded-full border border-white/10 text-gray-400 text-sm" data-testid={`jarvis-request-deny-${r.code}`}>Decline</button>
          <button onClick={() => decideReq(r.code, "approve")} className={`${HUD.btn} h-9 px-4 text-sm`} data-testid={`jarvis-request-approve-${r.code}`}>Approve</button>
        </div>
      ))}
      <NewDraft onDone={load} />
      {drafts.length === 0 ? (
        <p className={`${HUD.panel} p-5 text-sm text-gray-500`}>No drafts awaiting approval. New customer texts get an AI-drafted reply here.</p>
      ) : (
        drafts.map((d) => <Draft key={d.msg_id} d={d} onDone={load} />)
      )}
      {history.length > 0 && (
        <div className={`${HUD.panel} divide-y divide-cyan-400/5`}>
          <p className={`${HUD.label} p-4`}>Sent log</p>
          {history.map((h) => (
            <div key={h.msg_id} className="p-4 flex items-start gap-3">
              <span className={`${HUD.mono} text-[9px] tracking-[0.2em] px-2 py-0.5 rounded-full border uppercase shrink-0 ${STATUS[h.status] || STATUS.stub}`}>{h.status}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white">{h.to_name || h.to} <span className="text-gray-500 text-xs">· {timeAgo(h.sent_at || h.created_at)}</span></p>
                <p className="text-xs text-gray-400 break-words">{h.content}</p>
                {h.error && <p className="text-xs text-red-300 mt-1">{h.error}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default JarvisApprovals;
